import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tooltip, Popconfirm, Button, Form, Input } from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import EditableCell from './EditableCell';

const TableBuilder = ({
  title,
  data,
  columns,
  searchableColumns,
  sortableColumns,
  columnColors,
  typfilter,
  expandable,
  loading,
  handleSave,
  handleDelete,
  multipleSelect,
  singleSelect,
  onSelectionChange,
  paginationPos
}) => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = React.useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const isEditing = record => record.key === editingKey;

  const edit = record => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const rowSelection = useMemo(() => {
    if (!multipleSelect && !singleSelect) return null;

    return {
      type: singleSelect ? 'radio' : 'checkbox',
      selectedRowKeys,
      onChange: (newSelectedRowKeys, selectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        if (onSelectionChange) {
          onSelectionChange(newSelectedRowKeys, selectedRows);
        }
      },
      getCheckboxProps: record => ({
        name: record.name,
      }),
    };
  }, [multipleSelect, singleSelect, selectedRowKeys, onSelectionChange]);

  const save = async key => {
    try {
      const row = await form.validateFields();
      // Dispatch edit action or call API to update data here
      setEditingKey('');
      handleSave(key, row);
    } catch (errInfo) {
      console.error('Validation failed:', errInfo);
    }
  };

  const mergedColumns = useMemo(() => {
    return columns.map(col => {
      if (!col.editable) return col;
      return {
        ...col,
        onCell: record => ({
          record,
          inputType: col.dataIndex === 'age' ? 'number' : col?.inputType === "date" ? 'date' : 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          form
        }),
      };
    }).concat({
      title: 'Operation',
      dataIndex: 'operation',
      width: '20%',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Tooltip title="Speichern">
              <Button onClick={() => save(record.key)} style={{ marginRight: 8 }}>
                <SaveOutlined />
              </Button>
            </Tooltip>
            <Popconfirm title="Abbrechen?" onConfirm={cancel}>
              <Button icon={<CloseCircleOutlined />} />
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Tooltip title="Bearbeiten">
              <Button disabled={editingKey !== ''} onClick={() => edit(record)}>
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip title="Löschen">
              <Popconfirm
                title="Eintrag löschen?"
                onConfirm={() => handleDelete(record.key, record.blobId)}
              >
                <Button icon={<DeleteOutlined />} style={{ marginLeft: 8 }} />
              </Popconfirm>
            </Tooltip>
          </span>
        );
      },
    });
  }, [columns, editingKey, handleDelete, handleSave]);

  return (
    <Form form={form} component={false}>
      <Table
        title={() => <strong>{title}</strong>}
        components={{
          body: {
            cell: props => <EditableCell {...props} form={form} />
          }
        }}
        scroll={{ x: "max-content" }}
        bordered
        dataSource={data}
        columns={mergedColumns}
        rowSelection={multipleSelect && {
          type: "checkbox",
          ...rowSelection,
        }}
        rowClassName="editable-row"
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          position: paginationPos || ["bottomLeft"],
        }}
        expandable={
          expandable
            ? {
                expandedRowRender: record => (
                  <p style={{ margin: 0 }}>{record.description}</p>
                ),
                rowExpandable: record => record.description !== ''
              }
            : undefined
        }
        loading={loading}
      />
    </Form>
  );
};

export default TableBuilder;
